import { Box, Chip, CircularProgress, Fade, Stack, Typography } from "@mui/material";
import {
  GetPlayingState,
  GetFrameRate,
  PlayPause,
  GetPlayerImage,
  GoToFrame,
  GetPlaybackSpeed,
  GetRealCurrentFrame,
  GetTotalFrameCount,
} from "../../../Redux/Reducers/redux-slice-react-player";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { useSelector } from "react-redux";
import { WatermarkTemplates } from "../../../Core/Api/Types/watermark-templates";
import { BigPlayButton } from "./playing-statuses";
import { PreviewContext } from "../../../Pages/admin/Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview";
import { SlowMotionVideo } from "@mui/icons-material";

export const PlayerDefaultOverlay = (props: { hasFirstPlayed: boolean; context: PreviewContext; hover: boolean }) => {
  const { hasFirstPlayed, context, hover } = props;
  const image = useAppSelector(GetPlayerImage);
  const playbackSpeed = useAppSelector(GetPlaybackSpeed);
  const [showPlaybackSpeed, setShowPlaybackSpeed] = useState(false);
  const playbackTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (hasFirstPlayed) setShowPlaybackSpeed(true);
    if (playbackTimeout.current) clearTimeout(playbackTimeout.current);
    playbackTimeout.current = setTimeout(() => {
      setShowPlaybackSpeed(false);
    }, 3000);
  }, [playbackSpeed]);

  return (
    <Stack
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        overflow: "hidden",
        //DF-7322 : safari bug, if player is overlayed by an interactive element, subtitles are not loaded
        userSelect: "none",
        pointerEvents: "none",
        zIndex: 3,
      }}
    >
      <Fade in={!hasFirstPlayed}>
        <Stack
          sx={{
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,.5)",
          }}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              backgroundImage: `url(${image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(100px)",
            }}
            justifyContent={"center"}
            alignItems={"center"}
          ></Box>
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              background: `url(${image})`,
              backgroundSize: "contain",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              boxShadow: "inset 0px 0px 50px 20px rgba(0,0,0,.5)",
            }}
            justifyContent={"center"}
            alignItems={"center"}
          ></Box>
          <Fade in={hover || context === PreviewContext.DriveFolderPreview}>
            <Stack
              sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
                background: `rgba(0,0,0,.3)`,
              }}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <BigPlayButton hover={hover} />
            </Stack>
          </Fade>
        </Stack>
      </Fade>
      <Fade in={showPlaybackSpeed}>
        <Chip
          icon={<SlowMotionVideo />}
          label={`${playbackSpeed}x`}
          size="large"
          sx={(t) => ({
            position: "absolute",
            top: t.spacing(4),
            right: t.spacing(4),
            backgroundColor: t.palette.background[200],
            color: t.palette.text.primary,
            ...t.typography.subtitle2,

            ".MuiChip-icon": {
              color: t.palette.text.tier,
            },
          })}
        />
      </Fade>
    </Stack>
  );
};

export const PlayerBufferingOverlay = () => {
  return (
    <Stack
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        background: "rgba(0,0,0,.7)",
        justifyContent: "center",
        alignItems: "center",
        userSelect: "none",
        pointerEvents: "none",
      }}
    >
      <CircularProgress size={48} color="primary" />
    </Stack>
  );
};

const fakeWaveForm = Array.from({ length: 100 }, () => Math.random() * (0.7 - 0.3 + 1) + 0.3);

export const AudioOverlay = () => {
  const isPlaying = useAppSelector(GetPlayingState);
  const frameRate = useSelector(GetFrameRate);
  const totalFrameCount = useSelector(GetTotalFrameCount);
  const progress = (useSelector(GetRealCurrentFrame) / totalFrameCount) * 100;
  const waveContainer = useRef<HTMLDivElement>(null);
  const [baseWaveHeight, setBaseWaveHeight] = useState(20);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((event) => {
      setBaseWaveHeight(event[0].contentBoxSize[0].blockSize / 3);
    });

    if (waveContainer.current) resizeObserver.observe(waveContainer.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [waveContainer.current]);

  return (
    <Stack
      direction="row"
      justifyContent={"space-between"}
      alignItems={"center"}
      ref={waveContainer}
      sx={(theme) => ({
        pointerEvents: "none",
        background: "#000",
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        top: 0,
        flexGrow: 1,
        zIndex: 0,
      })}
    >
      {/* {[...Array(Math.floor(duration / frameRate / 30))].map((x, i) => ( */}
      {fakeWaveForm.map((x, i) => (
        <Stack
          sx={(theme) => ({
            height: fakeWaveForm[i] * baseWaveHeight,
            width: 2,
            background:
              //   progress >= (i / (duration / frameRate / 30)) * 100
              progress >= (i / fakeWaveForm.length) * 100 ? theme.palette.info.main : theme.palette.background[200],
            ...(isPlaying && { animation: "wave ease-in-out .4s infinite", animationDelay: `${fakeWaveForm[i]}s` }),
            "@keyframes wave": {
              "0%": { transform: "scaleY(1)" },
              "25%": { transform: "scaleY(.9)" },
              "50%": { transform: "scaleY(1)" },
              "75%": { transform: "scaleY(1.1)" },
              "100%": { transform: "scaleY(1)" },
            },
          })}
        ></Stack>
      ))}
    </Stack>
  );
};

export const WatermarkOverlay = (props: { text: string; tpl: WatermarkTemplates }) => {
  const { text, tpl } = props;

  const watermarkJsx = () => {
    switch (tpl) {
      case WatermarkTemplates.Standard_Duplicated:
        return (
          <>
            <Typography sx={{ position: "absolute", left: "10%", top: "10%" }} variant="body1">
              {text}
            </Typography>
            <Typography sx={{ position: "absolute", right: "10%", top: "10%" }} variant="body1">
              {text}
            </Typography>
          </>
        );
      case WatermarkTemplates.Standard_Top_Right:
        return (
          <Typography sx={{ position: "absolute", right: "10%", top: "10%" }} variant="body1">
            {text}
          </Typography>
        );
      case WatermarkTemplates.Large_Centered:
        return <Typography variant="h3">{text}</Typography>;
    }
  };
  return (
    <Stack
      sx={{ position: "absolute", left: 0, right: 0, bottom: 0, top: 0, userSelect: "none" }}
      justifyContent={"center"}
      alignItems={"center"}
    >
      {watermarkJsx()}
    </Stack>
  );
};

export const CompleteOverlay = (props: { isComplete: boolean }) => {
  const image = useAppSelector(GetPlayerImage);
  const dispatch = useAppDispatch();
  const { isComplete } = props;

  const handleReplayMedia = () => {
    dispatch(GoToFrame(0));
    dispatch(PlayPause());
  };

  if (!isComplete) return <></>;

  return (
    <Fade in={isComplete}>
      <Stack
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          top: 0,
          overflow: "hidden",
          justifyContent: "center",
          alignItems: "center",
        }}
        onClick={() => handleReplayMedia()}
      >
        <Typography variant="h5" color="text.tier" mb={2}>
          Media complete
        </Typography>
        <BigPlayButton hover={false} isComplete={isComplete} />
      </Stack>
    </Fade>
  );
};
